export function formatBotResponseHTML(raw: string): string {
  const allowedTags = ["ImageResponseCard", "button", "img", "a"];
  const imageStyles = "margin-top:10px;margin-bottom:10px;width:100%";
  const hrStyle = "border:none;border-top:1px solid #334155;margin:16px 0;";
  const strongStyle = "font-weight:700";

  const escapeHTML = (str: string) =>
    str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  // Encode allowed tags
  let marked = raw.replace(
    new RegExp(`<\\/?(${allowedTags.join("|")})[^>]*>`, "g"),
    (match) => `__HTMLTAG__${btoa(match)}__ENDTAG__`
  );

  // Convert Markdown-style images
  marked = marked.replace(
    /!\[([^\]]*)\]\(\s*<?([^)\s]+)>?(?:\s+["'][^"']*["'])?\s*\)/g,
    (_, alt, url) => {
      if (!/^https?:/i.test(url)) return _;
      return `<img src="${url}" alt="${alt}" style="${imageStyles}"/>`;
    }
  );

  // Escape everything else
  let safe = escapeHTML(marked);

  // Normalize line endings — but don’t convert to <br/> yet!
  safe = safe
    .replace(/\r\n|\r/g, "\n") // normalize line endings
    .replace(/\n{2,}/g, "\n\n"); // keep paragraph spacing

  // Parse headings, lists, and horizontal rules
  const lines = safe.split("\n");
  let inList = false;
  const parsedLines: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i].trim();

    // Headings (#, ##, etc.)
    const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      if (inList) {
        parsedLines.push("</ul>");
        inList = false;
      }
      const level = headingMatch[1].length;
      const headingText = headingMatch[2];
      const colorStyle = level <= 5 ? ' style="color:#fff;"' : "";
      parsedLines.push(`<h${level}${colorStyle}>${headingText}</h${level}>`);
      continue;
    }

    // Lists (-, *, + item)
    if (/^[-*+]\s+/.test(line)) {
      if (!inList) parsedLines.push("<ul>");
      inList = true;
      parsedLines.push(`<li>${line.replace(/^[-*+]\s+/, "")}</li>`);
      continue;
    }

    // Horizontal rule (---)
    if (/^[-]{3,}$/.test(line)) {
      if (inList) {
        parsedLines.push("</ul>");
        inList = false;
      }

      const prev = parsedLines.pop();
      if (prev && !prev.startsWith("<")) {
        parsedLines.push(`<h2 style="color:#fff;">${prev}</h2>`);
      } else if (prev) {
        parsedLines.push(prev);
      }

      parsedLines.push(`<hr style="${hrStyle}"/>`);
      continue;
    }

    // End list if needed
    if (inList) {
      parsedLines.push("</ul>");
      inList = false;
    }

    if (line === "") {
      parsedLines.push("<br/>");
      continue;
    }

    parsedLines.push(line);
  }

  if (inList) parsedLines.push("</ul>");
  safe = parsedLines.join("\n");

  // Bold text (**bold**)
  safe = safe.replace(/\*\*(.+?)\*\*/g, (match, text, offset, full) => {
    const before = full[offset - 1];
    const after = full[offset + match.length];
    const isInline =
      (before && /[^\n]/.test(before)) || (after && /[^\n]/.test(after));
    const displayStyle = isInline ? "inline" : "block";
    return `<strong style="${strongStyle}; display:${displayStyle};">${text}</strong>`;
  });

  // Final <br/> conversion (after parsing lists & headings)
  safe = safe.replace(/\n{2,}/g, "<br/><br/>").replace(/\n/g, "<br/>");

  // Linkify plain URLs
  const urlRegex = /(https?:\/\/[^\s<]+)/g;
  safe = safe
    .split(/(<[^>]+>)/g)
    .map((chunk) =>
      chunk.startsWith("<")
        ? chunk
        : chunk.replace(
            urlRegex,
            '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
          )
    )
    .join("");

  // Decode allowed tags
  safe = safe.replace(/__HTMLTAG__(.*?)__ENDTAG__/g, (_, encoded) => {
    const decoded = atob(encoded).trim();

    if (decoded === "<ImageResponseCard>")
      return `<div class="iris-button-class-container--blk" style="margin-bottom: 20px">`;
    if (decoded === "</ImageResponseCard>") return `</div>`;

    if (decoded.startsWith("<button") || decoded.includes("<button")) {
      return decoded.replace(
        /<button(.*?)>/g,
        `<button class="iris-button-class"$1>`
      );
    }
    if (decoded === "</button>") return `</button>`;

    if (decoded.startsWith("<img") || decoded.includes("<img")) {
      return decoded;
    }

    return decoded;
  });

  safe = safe.replace(/(<br\s*\/?>\s*){2,}/gi, "<br/>");

  safe = safe.replace(
    /<div class="iris-button-class-container--blk"[^>]*>([\s\S]*?)<\/div>/g,
    (match, inner) => {
      let cleaned = inner.replace(/<br\s*\/?>/gi, "");
      cleaned = cleaned.replace(
        /<button(.*?)>(.*?)<\/button>/gi,
        (_: string, attrs: string, innerText: string) => {
          const trimmedText = innerText.trim().toLowerCase();
          const isYellow =
            trimmedText === "engage with icxeed" ||
            trimmedText === "submit a ticket"
              ? " yellow-btn"
              : "";

          const displayText =
            trimmedText === "engage with icxeed"
              ? `<img src="images/rocket-icon.svg" alt="" style="width:16px;height:16px;margin-right:6px;vertical-align:middle;"/>Engage with iCXeed`
              : trimmedText === "submit a ticket"
              ? `<img src="images/submit-ticket-icon.svg" alt="" style="width:16px;height:16px;margin-right:6px;vertical-align:middle;"/>Submit a Ticket`
              : innerText;

          return `<button class="iris-button-class${isYellow}"${attrs}>${displayText}</button>`;
        }
      );

      return `<div class="iris-button-class-container--blk" style="margin-bottom: 20px">${cleaned}</div>`;
    }
  );

  // Remove <br> inside <ul> ... </ul> lists
  safe = safe.replace(/<ul[^>]*>([\s\S]*?)<\/ul>/g, (match, inner) => {
    const cleaned = inner.replace(/<br\s*\/?>/gi, "");
    return `<ul>${cleaned}</ul>`;
  });

  return safe;
}
